import type { App, ComponentPublicInstance } from "vue";
import { ElMessage } from "element-plus";
import * as Sentry from "@sentry/vue";

const FALLBACK_ERROR_MESSAGE = "An unexpected error occurred.";
const displayedErrors = new WeakSet<object>();


function displayError(error: unknown, source: string, context?: Record<string, unknown>): void {
  if ((typeof error === "object" && error !== null) || typeof error === "function") {
    if (displayedErrors.has(error)) return;
    displayedErrors.add(error);
  }

  console.error(`Unhandled exception (${source})`, error, context);

  ElMessage({
    type: "error",
    message: FALLBACK_ERROR_MESSAGE,
    duration: 5000,
    showClose: true,
  });

  Sentry.withScope((scope) => {
    scope.setLevel("error");
    scope.setTag("exception.source", source);
    if (context) scope.setContext("exception_handler", context);
    Sentry.captureException(error);
  });
}

export function installGlobalExceptionHandler(app: App): void {
  app.config.errorHandler = (
    error: unknown,
    instance: ComponentPublicInstance | null,
    info: string
  ) => {
    displayError(error, "vue", {
      component: instance?.$options.name || "anonymous",
      lifecycle: info,
    });
  };

  window.addEventListener("error", (event) => {
    displayError(event.error ?? event.message, "window", {
      filename: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    displayError(event.reason, "unhandledrejection");
  });
}
